import {
  isOrderDetail,
  type OrderDetail,
  type OrderItemSaleReturnRequest,
} from "@billiard/contracts";

export interface OrderItemSaleReturnCommand {
  kind: "SALEABLE_ORDER_ITEM_RETURN";
  sessionId: string;
  itemId: string;
  skuId: string;
  productName: string;
  unitPriceFen: number;
  request: OrderItemSaleReturnRequest;
  baselineOrder: OrderDetail;
}

export interface OrderItemSaleReturnRecovery {
  ownerIdentityKey: string;
  command: OrderItemSaleReturnCommand;
  state: "CLAIMED" | "OUTCOME_UNKNOWN";
}

export type OrderItemSaleReturnRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: OrderItemSaleReturnRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "OTHER_SESSION" }
  | { status: "UNAVAILABLE" };

export const ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY =
  "billiard-erp:order-item-sale-return-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CONDITIONAL_MODULE_DISABLED",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "INVALID_ORDER_ITEM_QUANTITY",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ORDER_ITEM_NOT_FOUND",
  "ORDER_ITEM_RETURN_EXCEEDS_AVAILABLE",
  "ORDER_NOT_UNPAID",
  "PHYSICAL_DISPOSITION_UNSUPPORTED",
  "PRODUCT_NOT_INVENTORY_MANAGED",
  "SESSION_NOT_OPEN",
]);

let pending: OrderItemSaleReturnRecovery | null = null;
let dispatching: OrderItemSaleReturnRecovery | null = null;
let hydrated = false;
let unavailable = false;

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function bounded(value: unknown, minimum: number, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    [...value].length >= minimum &&
    [...value].length <= maximum
  );
}

export function orderItemSaleReturnIdentityKey(
  operator: { tenantId: string; storeId: string; id: string },
  sessionId: string,
  itemId: string,
): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id, sessionId, itemId]);
}

function identityParts(value: unknown): readonly [string, string, string, string, string] | null {
  if (!bounded(value, 1, 2_048)) return null;
  try {
    const parts = JSON.parse(value) as unknown;
    return Array.isArray(parts) &&
      parts.length === 5 &&
      parts.every((part) => bounded(part, 1, 256))
      ? (parts as [string, string, string, string, string])
      : null;
  } catch {
    return null;
  }
}

function validRequest(value: unknown): value is OrderItemSaleReturnRequest {
  if (
    !record(value) ||
    !exactKeys(value, [
      "itemId",
      "quantity",
      "physicalDisposition",
      "paymentResolution",
      "reason",
      "idempotencyKey",
    ])
  ) {
    return false;
  }
  return (
    bounded(value.itemId, 1, 128) &&
    Number.isSafeInteger(value.quantity) &&
    (value.quantity as number) >= 1 &&
    (value.quantity as number) <= 99 &&
    (value.physicalDisposition === "SALEABLE_RETURNED" ||
      value.physicalDisposition === "UNSALEABLE_RETURNED") &&
    value.paymentResolution === "UNPAID_ORDER_REDUCTION" &&
    bounded(value.reason, 2, 200) &&
    bounded(value.idempotencyKey, 8, 128)
  );
}

function validCommand(value: unknown): value is OrderItemSaleReturnCommand {
  if (
    !record(value) ||
    !exactKeys(value, [
      "kind",
      "sessionId",
      "itemId",
      "skuId",
      "productName",
      "unitPriceFen",
      "request",
      "baselineOrder",
    ]) ||
    value.kind !== "SALEABLE_ORDER_ITEM_RETURN" ||
    !bounded(value.sessionId, 1, 128) ||
    !bounded(value.itemId, 1, 128) ||
    !bounded(value.skuId, 1, 128) ||
    !bounded(value.productName, 1, 200) ||
    !Number.isSafeInteger(value.unitPriceFen) ||
    (value.unitPriceFen as number) < 0 ||
    !validRequest(value.request) ||
    !isOrderDetail(value.baselineOrder) ||
    value.baselineOrder.items.some(
      (item) =>
        Object.hasOwn(item, "unitCostFen") ||
        Object.hasOwn(item, "costSnapshotKnown") ||
        Object.hasOwn(item, "inventoryCostSnapshot"),
    )
  ) {
    return false;
  }
  const item = value.baselineOrder.items.find((candidate) => candidate.id === value.itemId);
  return (
    value.baselineOrder.sessionId === value.sessionId &&
    value.baselineOrder.status === "OPEN" &&
    value.baselineOrder.paidFen === 0 &&
    value.baselineOrder.refundedFen === 0 &&
    value.baselineOrder.payments.length === 0 &&
    value.baselineOrder.refunds.length === 0 &&
    value.request.itemId === value.itemId &&
    item !== undefined &&
    item.skuId === value.skuId &&
    item.name === value.productName &&
    item.unitPriceFen === value.unitPriceFen &&
    value.request.quantity <= item.quantity - item.returnedQuantity
  );
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object") {
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: OrderItemSaleReturnCommand,
  state: OrderItemSaleReturnRecovery["state"],
): OrderItemSaleReturnRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: deepFreeze(structuredClone(command)),
    state,
  });
}

function parse(value: unknown): OrderItemSaleReturnRecovery | null {
  if (
    !record(value) ||
    !exactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !record(value.recovery) ||
    !exactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    typeof value.recovery.ownerIdentityKey !== "string" ||
    !identityParts(value.recovery.ownerIdentityKey) ||
    !validCommand(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function equals(left: OrderItemSaleReturnRecovery, right: OrderItemSaleReturnRecovery): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function save(recovery: OrderItemSaleReturnRecovery): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    target.setItem(
      ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const encoded = target.getItem(ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY);
    const stored = encoded === null ? null : parse(JSON.parse(encoded) as unknown);
    return stored !== null && equals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    unavailable = true;
    return;
  }
  try {
    const encoded = target.getItem(ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const restored = parse(JSON.parse(encoded) as unknown);
    if (!restored) {
      unavailable = true;
      return;
    }
    pending =
      restored.state === "CLAIMED"
        ? immutableRecovery(restored.ownerIdentityKey, restored.command, "OUTCOME_UNKNOWN")
        : restored;
    if (!save(pending)) unavailable = true;
  } catch {
    unavailable = true;
  }
}

export function readOrderItemSaleReturnRecovery(
  operator: { tenantId: string; storeId: string; id: string },
  sessionId: string | null,
): OrderItemSaleReturnRecoveryView {
  hydrate();
  if (unavailable) return { status: "UNAVAILABLE" };
  if (!pending) return { status: "NONE" };
  const parts = identityParts(pending.ownerIdentityKey);
  if (!parts) return { status: "UNAVAILABLE" };
  if (parts[0] !== operator.tenantId || parts[1] !== operator.storeId || parts[2] !== operator.id) {
    return { status: "OTHER_OPERATOR" };
  }
  return sessionId !== null && parts[3] === sessionId
    ? { status: "OWNED", recovery: pending }
    : { status: "OTHER_SESSION" };
}

export function claimOrderItemSaleReturnRecovery(
  identityKey: string,
  command: OrderItemSaleReturnCommand,
): OrderItemSaleReturnRecoveryView {
  hydrate();
  if (unavailable) return { status: "UNAVAILABLE" };
  if (pending) {
    return pending.ownerIdentityKey === identityKey
      ? { status: "OWNED", recovery: pending }
      : { status: "OTHER_SESSION" };
  }
  if (!identityParts(identityKey) || !validCommand(command)) {
    unavailable = true;
    return { status: "UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!save(recovery)) {
    unavailable = true;
    return { status: "UNAVAILABLE" };
  }
  pending = recovery;
  return { status: "OWNED", recovery };
}

function verify(recovery: OrderItemSaleReturnRecovery, identityKey: string): boolean {
  hydrate();
  if (unavailable || pending !== recovery || recovery.ownerIdentityKey !== identityKey)
    return false;
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    const encoded = target.getItem(ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY);
    const stored = encoded === null ? null : parse(JSON.parse(encoded) as unknown);
    return stored !== null && equals(stored, recovery);
  } catch {
    unavailable = true;
    return false;
  }
}

export function beginOrderItemSaleReturnDispatch(
  recovery: OrderItemSaleReturnRecovery,
  identityKey: string,
): boolean {
  if (dispatching || !verify(recovery, identityKey)) return false;
  dispatching = recovery;
  return true;
}

export function finishOrderItemSaleReturnDispatch(
  recovery: OrderItemSaleReturnRecovery,
  identityKey: string,
): boolean {
  if (dispatching !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatching = null;
  return true;
}

export function markOrderItemSaleReturnOutcomeUnknown(
  recovery: OrderItemSaleReturnRecovery,
  identityKey: string,
): boolean {
  if (!verify(recovery, identityKey)) return false;
  const unknown = immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!save(unknown)) {
    unavailable = true;
    return false;
  }
  pending = unknown;
  return true;
}

export function clearOrderItemSaleReturnRecovery(
  recovery: OrderItemSaleReturnRecovery,
  identityKey: string,
): boolean {
  if (!verify(recovery, identityKey)) return false;
  if (typeof window !== "undefined") {
    const target = storage();
    if (!target) return false;
    try {
      target.removeItem(ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY);
      if (target.getItem(ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      unavailable = true;
      return false;
    }
  }
  pending = null;
  return true;
}

export function orderItemSaleReturnOutcomeIsUnknown(error: unknown): boolean {
  if (!record(error)) return true;
  if (error.code === "GATEWAY_RESPONSE_INVALID" || error.code === "GATEWAY_UNREACHABLE")
    return true;
  if (typeof error.statusCode === "number") {
    if (error.statusCode === 408 || error.statusCode === 429 || error.statusCode >= 500)
      return true;
    if (error.statusCode >= 400) return false;
  }
  return !(typeof error.code === "string" && definiteNoEffectCodes.has(error.code));
}

export function resetOrderItemSaleReturnRecoveryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
  try {
    storage()?.removeItem(ORDER_ITEM_SALE_RETURN_RECOVERY_STORAGE_KEY);
  } catch {
    // The test reset also clears the fail-closed in-memory state.
  }
}

export function resetOrderItemSaleReturnRecoveryMemoryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
}
